import React from 'react';
import styled from 'astroturf/react';

type Props = {
  // Display name for author
  displayName: string;
  // University of author
  university?: string;
  // Number of followers
  followers?: number;
  // Whether the current user follows this author
  following?: boolean;
  // Url for author profile pic
  photo?: string;
  onFollow: () => void;
  onMessage: () => void;
  // Number of works by this author
  workCount?: number;
};

const PLACEHOLDER_PHOTO = '//a.academia-assets.com/images/s65_no_pic.png';

const Button = styled.button`
  align-items: center;
  background: #428bca;
  border: 0;
  border-radius: 2px;
  color: #fff;
  display: flex;
  height: 34px;
  justify-content: center;
  width: 100px;
  transition: 160ms background-color;

  &:hover {
    background: #3071a9;
  }

  &:not(:last-of-type) {
    margin-right: 15px;
  }

  &.secondary {
    background: #676767;
  }

  &.secondary:hover {
    background: #414141;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  padding: 10px 15px;
`;

const Container = styled.div`
  align-items: center;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 2px;
  box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  position: absolute;
  left: 50%;
  max-width: 300px;
  top: calc(100% + 10px);
  transform: translate(-50%);
  z-index: 2;

  // Top triangle
  &::before {
    background: #fff;
    border-left: 1px solid #ccc;
    border-top: 1px solid #ccc;
    content: '';
    height: 12px;
    left: calc(50% - 6px);
    position: absolute;
    top: -7px;
    transform: rotate(45deg);
    width: 12px;
  }

  &::after {
    content: '';
    height: 14px;
    left: 0;
    position: absolute;
    top: -14px;
    width: 100%;
  }
`;

const Image = styled.img`
  border-radius: 50%;
  display: block;
  height: 65px;
  object-fit: cover;
  width: 65px;
`;

const Label = styled.p`
  color: #878787;
  font-size: 11px;
  margin: 0;
`;

const Name = styled.h1`
  color: #0066cc;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.3em;
  margin: 10px 5px 2px 5px;
  text-align: center;
`;

const StatContainer = styled.div`
  align-items: center;
  border-bottom: 1px solid #ddd;
  border-top: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  padding: 8px 4px;

  &:not(:first-of-type) {
    border-left: 1px solid #ddd;
  }
`;

const Stats = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Text = styled.div`
  color: #494848;
  font-size: 11px;
  line-height: 1.4;
  text-align: center;
`;

const TopWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 35px 20px 8px;

  &:hover {
    cursor: pointer;
  }

  &:hover h1 {
    text-decoration: underline;
  }
`;

const Value = styled.p`
  font-weight: 500;
  margin: 0 0 2px;
`;

const formatter = new Intl.NumberFormat();
// @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
const format = (num: string | number) => formatter.format(num);

const Stat = ({ label, value }: { label: string; value: string | number }) => (
  <StatContainer>
    <Value>{format(value)}</Value>
    <Label>{label}</Label>
  </StatContainer>
);

const AuthorPopover = ({
  displayName,
  university,
  followers,
  following,
  photo,
  onFollow,
  onMessage,
  workCount,
}: Props) => (
  <Container>
    {/* TopWrapper is used so we can click anywhere in the top half of the popover
     * and be taken to the author's page
     */}
    <TopWrapper>
      <Image src={photo || PLACEHOLDER_PHOTO} />
      <Name>{displayName}</Name>
      <Text>{university}</Text>
    </TopWrapper>

    <Stats>
      {followers != null && <Stat label="Followers" value={followers} />}
      {workCount != null && <Stat label="Works" value={workCount} />}
    </Stats>

    <ButtonGroup>
      <Button
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        secondary
        onClick={(e) => {
          e.preventDefault();
          onMessage();
        }}
      >
        Message
      </Button>
      <Button
        onClick={(e) => {
          e.preventDefault();
          onFollow();
        }}
      >
        {following ? 'Following' : 'Follow'}
      </Button>
    </ButtonGroup>
  </Container>
);

export default AuthorPopover;
